export enum LOCALE {
  ZH = 'zh',
  EN = 'en',
}

export enum LANGUAGE {
  AUTO = 'auto',
  ZH = 'zh',
  EN = 'en',
  JA = 'ja',
  KO = 'ko',
  TH = 'th',
  FR = 'fr',
  DE = 'de',
  ES = 'es',
  AR = 'ar',
  ID = 'id',
  VI = 'vi',
  PT = 'pt',
  IT = 'it',
  NL = 'nl',
  RU = 'ru',
  KM = 'km',
  LO = 'lo',
  MY = 'my',
  CEB = 'ceb',
  FIL = 'fil',
  CS = 'cs',
  PL = 'pl',
  FA = 'fa',
  HE = 'he',
  TR = 'tr',
  HI = 'hi',
  BN = 'bn',
  UR = 'ur',
  YUE = 'yue',
}
export enum CACHE_TYPE {
  TEMPORARY = 'temporary',
  SESSION_STORAGE = 'sessionStorage',
  LOCAL_STORAGE = 'localStorage',
}

export type AfterTranslateCallback = (config: {
  sources: string[];
  targets: string[];
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  requestId: string;
}) => void;

export interface SignedRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
}

export interface TranslateRequestParams {
  sourceLanguage: string;
  targetLanguage: string;
  streaming: boolean;
  text: Record<string, string>;
  scene: string;
  fallbackTimeoutMs: number;
  appName: string;
  ext?: {
    terminologies?: Array<{ src: string; tgt: string }>;
    examples?: Array<{ src: string; tgt: string }>;
    domainHint?: string;
  };
}

export type GetTokenFunction = (params: TranslateRequestParams) => Promise<SignedRequest>;

export type TranslateFunction = (
  sources: string[],
  config: { type: string; isRetry: boolean },
) => Promise<string[]>;

export interface SDKConfig {
  locale?: LOCALE;

  getToken?: GetTokenFunction;

  terminologies?: Array<{ src: string; tgt: string }>;
  examples?: Array<{ src: string; tgt: string }>;
  domainHint?: string;

  chunkSize?: number;
  maxRetries?: number;
  afterTranslate?: AfterTranslateCallback;

  cache?: CacheOptions | boolean;

  translate?: TranslateFunction;

  plugins?: TranslatePlugin[];
}

export interface BaseTranslateConfig {
  srcLanguage?: LANGUAGE;
  tgtLanguage: LANGUAGE;
  lazyload?: boolean;
  lazyOffset?: number;
  lazySelectors?: string | string[];
  targetSelectors?: string | string[];
  excludeSelectors?: string | string[];
  dynamic?: boolean;
  translateDelay?: number;
}

export interface PageTranslateConfig extends BaseTranslateConfig {
  /** 需要翻译的元素属性，如 placeholder、title、aria-label */
  translateAttributes?: string | string[];
  rules?: Omit<TransStyleRule, 'transStyle'>[];
  onBeforeInsertTrans?: OnBeforeInsertPurePage;
  onTranslatingStart?: OnTranslatingPurePageStart;
  onTranslatingEnd?: OnTranslatingPurePageEnd;
}

export interface TransStyleRule {
  /** 匹配段落 commonAncestor 或其祖先的选择器，匹配到的节点作为“父容器”应用 style/className */
  selector: string;
  /** 应用到 <trans> 节点上的样式 */
  transStyle?: Record<string, string>;
  /** 应用到 selector 匹配到的节点（父容器）上的样式 */
  style?: Record<string, string>;
  /** 应用到 selector 匹配到的节点（父容器）上的 class */
  className?: string;
}

export interface TransInsertContext {
  /** 当前段落信息 */
  paragraph: import('./utils/dom').Paragraph;
  /** 译文纯文本 */
  translatedText: string;
  /** 即将/已插入的 trans 节点 */
  transNode: HTMLElement;
  /** 插入位置锚点节点 */
  anchor: Node | null;
  /** 源文 hash，用于回滚关联 */
  sourceHash: string;
  /** 可回滚 DOM 变更操作器 */
  mutator: RevertableMutator;
}

export type OnBeforeInsertTrans = (
  ctx: TransInsertContext,
) => void | HTMLElement | Promise<void | HTMLElement>;

export interface TranslatingContext {
  /** 当前段落信息 */
  paragraph: import('./utils/dom').Paragraph;
  /** 源文 hash，用于回滚关联 */
  sourceHash: string;
  /** 插入位置锚点节点 */
  anchor: Node | null;
  /** 可回滚 DOM 变更操作器 */
  mutator: RevertableMutator;
}

export interface RevertableMutator {
  setStyle(el: HTMLElement, prop: string, value: string, priority?: string): void;
  addClass(el: HTMLElement, className: string): void;
  removeClass(el: HTMLElement, className: string): void;
  appendNode(parent: Node, node: Node): void;
  removeNode(node: Node): void;
}

export type OnTranslatingStart = (ctx: TranslatingContext) => void | Promise<void>;
export type OnTranslatingEnd = (ctx: TranslatingContext) => void | Promise<void>;

export interface PurePageTransInsertContext {
  textNode: Text;
  translatedText: string;
  transNode: HTMLElement;
  anchor: Node | null;
  sourceHash: string;
  mutator: RevertableMutator;
}

export type OnBeforeInsertPurePage = (
  ctx: PurePageTransInsertContext,
) => void | HTMLElement | Promise<void | HTMLElement>;

export interface PurePageTranslatingContext {
  textNode: Text;
  sourceHash: string;
  anchor: Node | null;
  mutator: RevertableMutator;
}

export type OnTranslatingPurePageStart = (
  ctx: PurePageTranslatingContext,
) => void | Promise<void>;
export type OnTranslatingPurePageEnd = (
  ctx: PurePageTranslatingContext,
) => void | Promise<void>;

export interface ParagraphTranslateConfig extends BaseTranslateConfig {
  rules?: TransStyleRule[];
  extraBlockSelectors?: string | string[]; // 额外的选择器，匹配的元素将作为 block 元素，独占一行。
  extraInlineSelectors?: string | string[]; // 额外的选择器，匹配的元素将作为 inline 元素。
  onBeforeInsertTrans?: OnBeforeInsertTrans;
  onTranslatingStart?: OnTranslatingStart;
  onTranslatingEnd?: OnTranslatingEnd;
}

export interface TranslatePlugin {
  name: string;
  init(ctx: PluginContext, config: PageTranslateConfig | ParagraphTranslateConfig): void | Promise<void>;
  destroy(): void;
  remove?(): void;
}

export interface PluginContext {
  config: import('./core/config').ConfigManager;
  queue: import('./service/translate-queue').TranslateQueue;
  language: import('./service/language').LanguageService;
  events: import('./core/event-bus').EventBus;
  i18n: import('./core/i18n').I18nManager;
  createViewportObserver(options: ViewportObserverOptions): import('./core/viewport-observer').ViewportObserver;
  createDOMChangeDetector(options: DOMChangeDetectorOptions): import('./core/dom-change-detector').DOMChangeDetector;
}

export interface ViewportObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  offset?: number;
}

export interface DOMChangeDetectorOptions {
  target: Node;
  ignoreSelector?: string;
  attributeFilter?: string[];
  debounceMs?: number;
  onDetect: (entries: MutationRecord[]) => void;
}

export interface TranslateTask {
  id: string;
  text: string;
  srcLang: string;
  tgtLang: string;
  format: 'text' | 'html';
  resolve: (translation: string) => void;
  reject: (error: Error) => void;
}

export interface CacheOptions {
  type?: CACHE_TYPE;
  expireTime?: number;
}

export interface CacheConfig {
  enabled: boolean;
  type: CACHE_TYPE;
  expireTime: number;
}

export interface CacheEntry {
  translation: string;
  timestamp: number;
  sourcePrefix: string;
}

export interface CacheLookupResult {
  hits: Map<number, string>;
  misses: Array<{ index: number; text: string }>;
}
